/**
 * NOVA 바코드 생성기·파서 (DOM-SYS-001)
 * <p>
 * 형식: NOVA|{"T1":"VIN","T2":"VIN","V":"실제 값","U":"READ"}
 * <br>
 * 외부 의존성 없이 이 파일 하나로 동작한다. 모바일 앱에 이 파일을 그대로 준다.
 */

/** NOVA 바코드 접두어 */
export const NOVA_BARCODE_PREFIX = 'NOVA|';

/** 종류 → T1(type1), T2(type2). 새 종류는 여기에 한 줄 추가한다 */
export const NOVA_BARCODE_TYPES = {
    VIN: { T1: 'VIN', T2: 'VIN' },
    TASK_NO: { T1: 'TASK', T2: 'TASK_NO' },
} as const;

/** 용도. 새 용도는 여기에 한 줄 추가한다 */
export const NOVA_BARCODE_USAGES = {
    /** 단순조회 */
    READ: 'READ',
    /** 차량이동 */
    MOVE: 'MOVE',
    /** 작업목록 */
    TASK_ALL: 'TASK_ALL',
    /** 사진업로드 */
    TASK_UPLOAD: 'TASK_UPLOAD',
    /** NOVA 바코드가 아닌 값을 스캔했을 때. 무엇을 할지 앱이 정한다 */
    ACTION: 'ACTION',
} as const;

export type NovaBarcodeType = keyof typeof NOVA_BARCODE_TYPES;
export type NovaBarcodeUsage = keyof typeof NOVA_BARCODE_USAGES;

/** 파서 결과 */
export interface NovaBarcode {
    /** 종류 1단계 */
    T1: string;
    /** 종류 2단계 */
    T2: string;
    /** 실제 값 */
    V: string;
    /** 용도 */
    U: string;
}

/**
 * NOVA 바코드가 아닌 값의 판정 규칙. 위에서부터 보고 처음 맞는 규칙을 쓴다.
 * 새 규칙은 기존 규칙과 구별되는지 확인하고 넣는다.
 */
export const NOVA_BARCODE_PLAIN_RULES: { name: string; pattern: RegExp; type: NovaBarcodeType; usage: NovaBarcodeUsage }[] = [
    // 작업번호 지금 형식. 예: T0001-0031, T12345-6789
    { name: '작업번호', pattern: /^T\d{4,}-\d{4}$/, type: 'TASK_NO', usage: 'TASK_UPLOAD' },
    // 작업번호 이전 형식. 예: T20260929-0000123
    { name: '작업번호(이전 형식)', pattern: /^T\d{8}-\d+$/, type: 'TASK_NO', usage: 'TASK_UPLOAD' },
    { name: 'VIN', pattern: /^[A-Za-z0-9]{17}$/, type: 'VIN', usage: 'ACTION' },
    // 짧은VIN(vin2, VIN 뒤 7자리). 서버 조회가 VIN과 함께 받으므로 VIN과 같은 종류로 돌려준다
    { name: '짧은VIN', pattern: /^[A-Za-z0-9]{7}$/, type: 'VIN', usage: 'ACTION' },
];

/**
 * NOVA 바코드 문자열을 만든다.
 *
 * @param value 실제 값. 바꾸지 않고 그대로 넣는다
 * @param type 종류 (NOVA_BARCODE_TYPES)
 * @param usage 용도 (NOVA_BARCODE_USAGES)
 * @throws 값이 비어 있거나 종류·용도가 목록에 없을 때
 */
export function encodeNovaBarcode(value: string, type: NovaBarcodeType, usage: NovaBarcodeUsage): string {
    if (value == null || String(value).trim() === '') {
        throw new Error('NOVA 바코드: 값이 비어 있습니다.');
    }
    const t = NOVA_BARCODE_TYPES[type];
    if (!t) {
        throw new Error(`NOVA 바코드: 없는 종류입니다. (${type})`);
    }
    if (!NOVA_BARCODE_USAGES[usage]) {
        throw new Error(`NOVA 바코드: 없는 용도입니다. (${usage})`);
    }
    const barcode: NovaBarcode = { T1: t.T1, T2: t.T2, V: String(value), U: NOVA_BARCODE_USAGES[usage] };
    return NOVA_BARCODE_PREFIX + JSON.stringify(barcode);
}

/**
 * 스캔한 문자열을 해석한다. 앞뒤 공백만 지우고 대소문자는 바꾸지 않는다.
 * <br>
 * encodeNovaBarcode로 만든 값뿐 아니라 NOVA 바코드가 아닌 값(VIN, 작업번호 등)도 판정한다.
 *
 * @returns {T1, T2, V, U}. 판정할 수 없으면 null
 */
export function decodeNovaBarcode(scanned: string | null | undefined): NovaBarcode | null {
    const text = String(scanned ?? '').trim();
    if (!text) {
        return null;
    }

    if (text.startsWith(NOVA_BARCODE_PREFIX)) {
        return decodeNovaJson(text.substring(NOVA_BARCODE_PREFIX.length));
    }

    const rule = NOVA_BARCODE_PLAIN_RULES.find(r => r.pattern.test(text));
    if (!rule) {
        return null;
    }
    const t = NOVA_BARCODE_TYPES[rule.type];
    return { T1: t.T1, T2: t.T2, V: text, U: NOVA_BARCODE_USAGES[rule.usage] };
}

/** NOVA| 뒤의 JSON. 깨졌거나 T1·T2·V·U 중 빈 것이 있으면 null */
function decodeNovaJson(json: string): NovaBarcode | null {
    let obj: any;
    try {
        obj = JSON.parse(json);
    } catch {
        return null;
    }
    if (obj == null || typeof obj !== 'object') {
        return null;
    }
    const keys: (keyof NovaBarcode)[] = ['T1', 'T2', 'V', 'U'];
    if (keys.some(k => typeof obj[k] !== 'string' || obj[k] === '')) {
        return null;
    }
    return { T1: obj.T1, T2: obj.T2, V: obj.V, U: obj.U };
}
